import {AlternateLocale} from '@aboutyou/config';
import {CountrySwitch_LanguageSelection} from '@aboutyou/grpc-clients/tadarida/aysa_api/features/country/v1/country_switch.types';
import {useLocale} from '@aboutyou/i18n';
import {createPath} from '@aboutyou/router';
import {exists} from '@aboutyou/utils';
import {useHistory} from 'react-router';
import {usePreferredLocale} from '../usePreferredLocale';

export const useLanguagesList = ({
  languageSelection,
  alternateLanguages,
}: {
  languageSelection?: CountrySwitch_LanguageSelection;
  alternateLanguages?: AlternateLocale[];
}) => {
  const locale = useLocale();
  const history = useHistory();
  const {setPreferredLocale} = usePreferredLocale();

  return languageSelection?.languages
    .map(language => {
      if (!language.link) {
        return null;
      }

      const backendUrl = new URL(language.link.url);
      const alternateLanguage = language.isSelected
        ? {locale, url: backendUrl.href}
        : findAlternateLanguage({
            backendUrl: backendUrl.href,
            alternateLanguages,
          });

      if (!alternateLanguage) {
        return null;
      }

      const currentUrlSearch = new URLSearchParams(history.location.search);
      currentUrlSearch.delete('force-language'); // to avoid duplicate query params
      const backendUrlSearch = new URLSearchParams(backendUrl.search);

      const targetSearch = new URLSearchParams(
        currentUrlSearch.toString() + '&' + backendUrlSearch.toString(),
      );

      const targetUrl = new URL(
        createPath({
          pathname: history.location.pathname,
          search: targetSearch.toString(),
        }),
        alternateLanguage.url,
      ).toString();

      return {
        isSelected: language.isSelected,
        label: language.label,
        linkTarget: targetUrl,
        onClick: (ev: MouseEvent) => {
          ev.preventDefault();
          setPreferredLocale(alternateLanguage);

          window.location.href = targetUrl;
        },
      };
    })
    .filter(exists);
};

/**
 * This helper function map the backend language URL to the available alternate language configuration on the frontend side, based on the environment.
 * Checking the host of the URL is sufficient for all environments except for preview, as the preview environment uses production data and therefore has the production backend URL.
 * In order to make it work for the preview environment as well, we need to remove the "-preview" from the URL to obtain the appropriate alternate language.
 *
 * i.e.
 * This backendUrl "https://en.aboutyou.de?force-language=1" should be mapped to this "https://en-preview.aboutyou.de?force-language=1" alternate language (from packages/core/config/src/urlMappings/preview.ts)
 */
const findAlternateLanguage = ({
  backendUrl,
  alternateLanguages,
}: {
  backendUrl: string;
  alternateLanguages?: AlternateLocale[];
}) => {
  const backendUrlHost = new URL(backendUrl).host;
  return alternateLanguages?.find(({url}) =>
    url.replace('-preview', '').includes(backendUrlHost),
  );
};
